/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { Track } from "@packages/sdk";
import { useRequestMe } from "@packages/sdk";
import type { Dispatch, SetStateAction } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import { spacing } from "../../../../../../global/emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import { SubscriptionIcon } from "../../../../../components/_base/icons/Icon/GeneralIcons";
import { type DropdownMenuItemProps } from "../../../../../components/_base/modals/DropdownMenuItem/DropdownMenuItem.emotion";
import { ModalDropdownMenuHeader } from "../../../../../components/_base/modals/ModalDropdownMenuHeader/ModalDropdownMenuHeader.emotion";
import { ModalDropdownMenuItem } from "../../../../../components/_base/modals/ModalDropdownMenuItem/ModalDropdownMenuItem.emotion";
import type { ModalItemProps } from "../../../../../components/_base/modals/ModalItem/ModalItem.emotion";

export type SelectLengthListProps = {
  selectedLength: Partial<Track>;
  setSelectedLength: Dispatch<SetStateAction<Partial<Track>>>;
  options: Partial<Track>[];
};

const menuSubheaderStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayMedium,
  height: "auto",
  justifyContent: "flex-start",
  paddingBlock: spacing.m,
  paddingInline: spacing.m,
  textAlign: "left",
  textTransform: "uppercase",
  ":first-of-type": {
    marginTop: "-4px",
  },
});
const menuItemStyle: Interpolation = {
  paddingLeft: spacing.xl,
};
const flexStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  rowGap: spacing.xxs,
};

export const SelectLengthList = ({
  options,
  selectedLength,
  setSelectedLength,
}: SelectLengthListProps) => {
  const t = useTranslations();
  const { query: me } = useRequestMe();

  const videoGroup = options.filter((trk) => trk.media_type === "video");
  const audioGroup = options.filter((trk) => !videoGroup.includes(trk));

  const optionMap = (option) => {
    const hasAccess =
      typeof option.has_access !== "undefined"
        ? option.has_access
        : (option.paid && me?.data?.subscription) || !option.paid;
    const iconProps: DropdownMenuItemProps | ModalItemProps = !hasAccess
      ? { startIcon: <SubscriptionIcon /> }
      : {};
    return (
      <ModalDropdownMenuItem
        key={`sessionDetails_selectLength_length_${option.id}`}
        selected={option.id === selectedLength?.id}
        onSelect={() => setSelectedLength(option)}
        dropdownMenuItemCSS={videoGroup.length && menuItemStyle}
        dropdownMenuItemProps={iconProps as DropdownMenuItemProps}
        modalItemCSS={videoGroup.length && menuItemStyle}
        modalItemProps={iconProps as ModalItemProps}
      >
        {option.length}
      </ModalDropdownMenuItem>
    );
  };

  return (
    <div css={flexStyle}>
      <ModalDropdownMenuHeader>{t("media_options")}</ModalDropdownMenuHeader>
      {videoGroup.length ? (
        <>
          <Text type={"title"} size={"s"} css={menuSubheaderStyle}>
            {t("media_type_video")}
          </Text>
          {videoGroup.map(optionMap)}
        </>
      ) : null}
      {audioGroup.length ? (
        <>
          <Text type={"title"} size={"s"} css={menuSubheaderStyle}>
            {t("media_type_audio")}
          </Text>
          {(audioGroup ?? []).map(optionMap)}
        </>
      ) : null}
    </div>
  );
};
